import { useEffect, useMemo, useState } from 'react';
import { collection, onSnapshot } from 'firebase/firestore';
import { db } from '../config/firebase';

/**
 * useUserDirectory — one live map of uid → user document, shared by every tab
 * whose records carry only a raw uid (orders.customerId, posts.userId,
 * pets.ownerId, reports.userId, vetBookings.userId, …).
 *
 * Why this exists: those collections are written by the mobile app and only
 * ever store the uid. The admin tables rendered that uid verbatim, so Orders
 * and Posts showed strings like `SpQr5VHfLcbiXLAeqD5wz7RzqR92` where a person's
 * name belongs. The old code read denormalised fields (`customerName`,
 * `userName`) that the app never writes, so every row fell through to the uid.
 *
 * The `users` collection is small (tens of documents) and the dashboard already
 * subscribes to it, so one extra listener is cheap and keeps names live when a
 * user renames themselves.
 */

// createUserProfile writes `name` / `middleName` / `surName` / `username`.
// displayName / firstName / lastName are legacy fields, kept as a fallback for
// any old document that still carries them.
export const userFullName = (u) => {
  if (!u) return '';
  return (
    [u.name, u.middleName, u.surName].filter(Boolean).join(' ').trim() ||
    u.displayName ||
    [u.firstName, u.lastName].filter(Boolean).join(' ').trim() ||
    ''
  );
};

export const userHandle = (u) =>
  u && u.username ? `@${String(u.username).replace(/^@/, '')}` : '';

export const userDisplayName = (u) =>
  userFullName(u) || userHandle(u) || (u && u.email) || '';

export const useUserDirectory = () => {
  const [byId, setById] = useState(() => new Map());
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const unsub = onSnapshot(
      collection(db, 'users'),
      (snap) => {
        const next = new Map();
        snap.docs.forEach((d) => next.set(d.id, { id: d.id, ...d.data() }));
        setById(next);
        setLoading(false);
      },
      (err) => {
        console.error('useUserDirectory: users subscription failed:', err);
        setLoading(false);
      }
    );
    return () => unsub();
  }, []);

  return useMemo(() => {
    const getUser = (uid) => (uid ? byId.get(uid) || null : null);

    /** Best human label for a uid. Falls back to the uid so nothing renders blank. */
    const nameOf = (uid, fallback = '') => {
      const u = getUser(uid);
      return (u && userDisplayName(u)) || fallback || uid || '';
    };

    const emailOf = (uid) => {
      const u = getUser(uid);
      return (u && u.email) || '';
    };

    const handleOf = (uid) => userHandle(getUser(uid));

    const avatarOf = (uid) => {
      const u = getUser(uid);
      return (u && (u.avatar || u.photoURL || u.profilePhoto)) || '';
    };

    /** Everything a search box should match for a uid, lowercased. */
    const searchBlobOf = (uid) => {
      const u = getUser(uid);
      if (!u) return (uid || '').toLowerCase();
      return [uid, userFullName(u), u.username, u.email]
        .filter(Boolean)
        .join(' ')
        .toLowerCase();
    };

    return { byId, loading, getUser, nameOf, emailOf, handleOf, avatarOf, searchBlobOf };
  }, [byId, loading]);
};

export default useUserDirectory;
